/**
 * SoccerKit — themed jersey art from embedded SVG templates.
 * `stripes` uses the striped shirt asset; all other kit types use the plain (sleeves) asset.
 * Colors are expanded to literal fills for react-native-svg (style defs are not applied on native).
 */

import { useId, useMemo } from 'react';
import { SvgAst, SvgXml } from 'react-native-svg';
import { normalizeTeamKitType, type TeamKitType } from '../../../constants/teamKitColors';
import { buildExpandedKitSvgXml } from './kitSvgExpandForNative';
import { PLAIN_KIT_SVG_TEMPLATE, STRIPED_KIT_SVG_TEMPLATE } from './kitSvgTemplates.generated';
import { getCachedKitAst, kitAstCacheKey } from './kitSvgAstCache';

export interface SoccerKitProps {
  primary: string;
  secondary: string;
  type?: TeamKitType;
  size?: number;
  number?: number;
}

/**
 * Below this rendered size the ambient drop-shadow is visually negligible but each
 * `feDropShadow` forces an offscreen render layer on react-native-svg (Android). Lists
 * that mount many small kits at once (e.g. the lineup pitch — ~40 kits) otherwise spike
 * native memory hard, so skip the filter for small kits and keep it only for hero sizes.
 */
const KIT_SHADOW_MIN_SIZE = 64;

function normalizeHexColor(value: string): string {
  const v = value.trim();
  if (v.startsWith('#')) {
    const hex = v.slice(1);
    if (/^[0-9a-fA-F]{6}$/.test(hex) || /^[0-9a-fA-F]{3}$/.test(hex)) return v;
  }
  if (/^[0-9a-fA-F]{6}$/.test(v) || /^[0-9a-fA-F]{3}$/.test(v)) return `#${v}`;
  return '#808080';
}

function isLightColor(hex: string): boolean {
  const h = normalizeHexColor(hex);
  const r = parseInt(h.slice(1, 3), 16) || 0;
  const g = parseInt(h.slice(3, 5), 16) || 0;
  const b = parseInt(h.slice(5, 7), 16) || 0;
  return r * 0.299 + g * 0.587 + b * 0.114 > 165;
}

function addKitNumber(xml: string, kitType: TeamKitType, number: number | undefined, primary: string): string {
  if (number == null || !Number.isFinite(number)) return xml;

  const label = String(Math.trunc(number));
  const isStriped = kitType === 'stripes';
  const x = isStriped ? 448 : 896;
  const y = isStriped ? 715 : 1430;
  const fontSize = isStriped ? 230 : 460;
  const fill = isLightColor(primary) ? '#111827' : '#FFFFFF';
  const stroke = fill === '#FFFFFF' ? '#111827' : '#FFFFFF';
  const text = `<text x="${x}" y="${y}" text-anchor="middle" dominant-baseline="middle" font-size="${fontSize}" font-weight="900" font-family="Arial, sans-serif" fill="${fill}" stroke="${stroke}" stroke-width="${Math.round(fontSize * 0.06)}" stroke-linejoin="round">${label}</text>`;

  return xml.replace('</svg>', `${text}</svg>`);
}

/** Per-instance filter id — duplicate `id="kit-ambient-shadow"` breaks multiple kits on the same HTML document (e.g. match details header on web). */
function addKitShadow(xml: string, shadowFilterId: string): string {
  const shadowDefs = `<defs><filter id="${shadowFilterId}" x="-18%" y="-18%" width="136%" height="136%"><feDropShadow dx="0" dy="14" stdDeviation="18" flood-color="#000000" flood-opacity="0.24"/><feDropShadow dx="0" dy="0" stdDeviation="10" flood-color="#000000" flood-opacity="0.18"/></filter></defs>`;

  return xml
    .replace(/(<svg\b[^>]*>)/, `$1${shadowDefs}<g filter="url(#${shadowFilterId})">`)
    .replace('</svg>', '</g></svg>');
}

export const SoccerKit = ({
  primary,
  secondary,
  type = 'plain',
  size = 100,
  number,
}: SoccerKitProps) => {
  const reactDomId = useId();
  const shadowFilterId = `kit-sh-${reactDomId.replace(/:/g, '')}`;
  const withShadow = size >= KIT_SHADOW_MIN_SIZE;

  const kitType = normalizeTeamKitType(type);
  const p = normalizeHexColor(primary);
  const s = normalizeHexColor(secondary);

  const buildXml = (shadowId: string | null): string => {
    const base = kitType === 'stripes' ? STRIPED_KIT_SVG_TEMPLATE : PLAIN_KIT_SVG_TEMPLATE;
    const expanded = buildExpandedKitSvgXml(base, p, s);
    return addKitNumber(shadowId ? addKitShadow(expanded, shadowId) : expanded, kitType, number, p);
  };

  // Hero kits keep a per-instance shadow filter id, so they cannot share a parsed tree.
  const shadowXml = useMemo(
    () => (withShadow ? buildXml(shadowFilterId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildXml is derived from these inputs
    [withShadow, shadowFilterId, kitType, p, s, number],
  );

  // Small kits (lists) share one parsed tree per type + colours + number (AC-3.8).
  const sharedAst = useMemo(
    () =>
      withShadow ? null : getCachedKitAst(kitAstCacheKey(kitType, p, s, number), () => buildXml(null)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildXml is derived from these inputs
    [withShadow, kitType, p, s, number],
  );

  const compactHeight = Math.round(size * 0.88);
  if (shadowXml !== null) {
    return <SvgXml xml={shadowXml} width={size} height={compactHeight} />;
  }
  return <SvgAst ast={sharedAst} override={{ width: size, height: compactHeight }} />;
};
